import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { MainBtn } from "../components/Btns";
import { GradientWord } from "../components/Headlines";
import { useContact } from "../contexts/ContactContext";
import { submitLead } from "../leads";
import { trackEvent } from "../analytics";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(2, 3, 18, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: ${(props) => props.theme.panel};
  border: 1px solid ${(props) => props.theme.accentBorder};
  border-radius: 20px;
  padding: 36px 32px 32px;
  position: relative;
  box-shadow: 0 30px 80px -30px ${(props) => props.theme.accent};
`;

const Close = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.hairline};
  background: transparent;
  color: ${(props) => props.theme.textMuted};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.theme.accentBorder};
    color: white;
  }
`;

const Title = styled.h2`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 28px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
  margin: 0 0 6px 0;
`;

const Sub = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 15px;
  line-height: 1.5;
  color: ${(props) => props.theme.textMuted};
  margin: 0 0 26px 0;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 16px;
`;

const FieldLabel = styled.span`
  display: block;
  font-family: ${(props) => props.theme.regularFont};
  font-size: 13px;
  color: ${(props) => props.theme.textMuted};
  margin-bottom: 6px;
`;

const inputStyles = (props) => `
  width: 100%;
  background: ${props.theme.darkBg1};
  border: 1px solid ${props.theme.hairline};
  border-radius: 10px;
  padding: 12px 14px;
  color: ${props.theme.textColor};
  font-family: ${props.theme.regularFont};
  font-size: 15px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props.theme.accent};
  }
`;

const Input = styled.input`
  ${(props) => inputStyles(props)}
`;

const Select = styled.select`
  ${(props) => inputStyles(props)}
  appearance: none;
  cursor: pointer;
`;

const Textarea = styled.textarea`
  ${(props) => inputStyles(props)}
  resize: vertical;
  min-height: 84px;
`;

const SubmitBtn = styled(MainBtn)`
  width: 100%;
  margin-top: 8px;
`;

const ErrorMsg = styled.p`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 13px;
  color: #ff8a8a;
  margin: 0 0 14px 0;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 20px 0 10px;
`;

const interestOptions = [
  "I'm not sure yet",
  "New website",
  "Ongoing maintenance",
  "Essential plan",
  "Growth plan",
  "Partner plan",
];

const emptyForm = {
  name: "",
  email: "",
  company: "",
  interest: interestOptions[0],
  message: "",
};

export default function ContactModal() {
  const { isOpen, plan, closeContact } = useContact();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  // When the modal opens, reset and pre-select the package if one was passed.
  useEffect(() => {
    if (!isOpen) return;
    const preset =
      plan && interestOptions.includes(`${plan} plan`)
        ? `${plan} plan`
        : interestOptions[0];
    setForm({ ...emptyForm, interest: preset });
    setStatus("idle");
  }, [isOpen, plan]);

  // Lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && closeContact();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, closeContact]);

  const update = useCallback(
    (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      await submitLead(form);
      setStatus("success");
      trackEvent("generate_lead", { interest: form.interest });
    } catch (err) {
      setStatus("error");
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onMouseDown={(e) => e.target === e.currentTarget && closeContact()}>
      <Card>
        <Close onClick={closeContact} aria-label="Close">
          ×
        </Close>

        {status === "success" ? (
          <SuccessWrap>
            <Title>
              Thanks - <GradientWord>talk soon</GradientWord>
            </Title>
            <Sub>
              We've got your details and will get back to you within one business
              day.
            </Sub>
            <SubmitBtn as="button" onClick={closeContact}>
              Close
            </SubmitBtn>
          </SuccessWrap>
        ) : (
          <>
            <Title>
              Let's <GradientWord>talk</GradientWord>
            </Title>
            <Sub>
              {plan
                ? `Enquiring about the ${plan} plan. Tell us a little about your site - we reply fast, usually same day.`
                : "Tell us a little about your site and what you need. We reply fast - usually same day."}
            </Sub>

            {status === "error" && (
              <ErrorMsg>
                Something went wrong sending that. Please try again, or email us
                at db@m87technologies.com.
              </ErrorMsg>
            )}

            <form onSubmit={handleSubmit}>
              <Field>
                <FieldLabel>Name *</FieldLabel>
                <Input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                />
              </Field>
              <Field>
                <FieldLabel>Work email *</FieldLabel>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                />
              </Field>
              <Field>
                <FieldLabel>Company *</FieldLabel>
                <Input
                  type="text"
                  required
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Company name"
                />
              </Field>
              <Field>
                <FieldLabel>Interested in</FieldLabel>
                <Select value={form.interest} onChange={update("interest")}>
                  {interestOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <FieldLabel>Anything else?</FieldLabel>
                <Textarea
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A line about your site, timeline, or what's bugging you."
                />
              </Field>
              <SubmitBtn
                as="button"
                type="submit"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Sending…" : "Send"}
              </SubmitBtn>
            </form>
          </>
        )}
      </Card>
    </Overlay>
  );
}
